import type { Container, Ticker } from 'pixi.js';
import type { Renderer } from 'pixi.js';
import type { CardDef } from '../../core/card';
import type { CardPose } from '../card/pose';
import { CardArtRegistry, CardFaceCache, faceSize, specArt } from '../card';
import type { CardView } from '../card/cardView';
import type { FaceSize } from '../card/metrics';
import type { FeelParams } from './feel';
import { HandView } from './handView';
import type { HandLayout } from './layout';

/**
 * **一块手牌 = 可以直接挂进场景的东西。**
 *
 * 这一层只是**装配**，一行手感/布局都没有。它把"每个屏幕都要写一遍"的那几件
 * 杂事包起来：
 *
 * ```
 *   Hand
 *    ├ 一个 CardFaceCache      ← 没给就自己建，并在 destroy 时负责销毁
 *    ├ 一个 HandView           ← 牌、指针、顺序、选中
 *    ├ 按牌高算出牌面尺寸       ← 长宽比只有 metrics.ts 说了算
 *    ├ 开局前把这一手的牌面烘完  ← 免得第一次见到某张牌时卡一帧
 *    └ update(ticker) / resize(bounds)
 * ```
 *
 * 游戏里就该这么用（`docs/card-feel.md` §11 有完整的接法）：
 *
 * ```ts
 * const hand = new Hand({
 *   input: app.stage,                    // ⚠️ 必须 eventMode='static' + 铺满 hitArea
 *   renderer: app.renderer,
 *   bounds: { width: LOGICAL_WIDTH, height: LOGICAL_HEIGHT },
 *   height: 150,
 *   cards: myHand,
 *   onPlay: (played) => game.play(played),
 * });
 * world.addChild(hand.container);
 * ticker.add((t) => hand.update(t));     // ⚠️ 全项目只有这一个 ticker 回调
 * ```
 *
 * ⚠️ **`bounds` 是逻辑画布尺寸，不是牌尺寸。** 一手牌摆在哪儿是从它推出来的
 * （x 居中、中心牌贴底、间距/垂度按张数算），所以窗口一变只要调 `resize()`。
 *
 * ⚠️ **`faces` 建议传进来共用。** 一个对局里手牌、桌面、弃牌堆、检视
 * 用的是**同一批牌面**，各建一份缓存等于把每张牌画好几遍
 * （`faceCache.ts` 开头讲的就是这个）。
 */
export interface HandOptions {
  /**
   * 在哪收指针事件。一般就是 `app.stage`。
   *
   * ⚠️ 必须 `eventMode = 'static'` 而且有一个铺满的 `hitArea` ——
   * 调试页和 `cardLab.ts` 都是这么设的。`Hand` 不替调用方改这两个值：
   * 那是场景的布局，不是手牌的事。
   */
  readonly input: Container;
  /** 渲染器。**只在没给 `faces` 时用得到**（要拿它的 resolution 建缓存） */
  readonly renderer: Renderer;
  /**
   * 牌面缓存。**多块手牌/多个牌位共用一份**更省显存。
   * 不给就自己建一份，并在 `destroy()` 时销毁它。
   */
  readonly faces?: CardFaceCache;
  /** 逻辑画布尺寸。手牌的摆法是从它推出来的 */
  readonly bounds: { readonly width: number; readonly height: number };
  /** 牌**高**（逻辑像素）。宽度按卡牌比例算 —— 形状只有 `metrics.ts` 说了算 */
  readonly height: number;
  /** 手牌里都有哪些牌 */
  readonly cards: readonly CardDef[];
  /**
   * 手动指定扇形锚点。
   *
   * ⚠️ 不给就按"x 居中、中心牌的底边贴着画布底边"推导 —— 正常的手牌都该这样。
   * 只有"摆在别处的一张牌"（比如检视、教学）才需要手动指。
   */
  readonly anchor?: { readonly x: number; readonly y: number };
  /** 想弯多少（单位：牌高）。不给用默认值 */
  readonly curl?: number;
  /** 手牌横向最多占画布多宽。不给用默认值 */
  readonly fill?: number;
  /** 手感参数。不给用默认值 */
  readonly feel?: Partial<FeelParams>;
  /** 拖出手牌范围松手时回调 */
  readonly onPlay?: (played: readonly CardDef[]) => void;
  /** 构造时就把这一手的牌面烘完。默认 true */
  readonly prewarm?: boolean;
}

export class Hand {
  /** 挂进场景的东西 */
  readonly container: Container;

  /**
   * 底下那台机器：选中、顺序、读数、调参都在它上面。
   *
   * 手感/布局的**参数**走 `applyFeel` / `applyLayout`，
   * **读数**走 `selected` / `order` / `played` / `layout` —— 这些 `Hand` 也转发了，
   * 一般不用直接摸 `view`。
   */
  readonly view: HandView;

  /** 牌面缓存。可能是传进来的那份，也可能是自己建的 */
  readonly faces: CardFaceCache;

  /** 牌**本身**的尺寸（不含出血） */
  readonly size: FaceSize;

  readonly #ownsFaces: boolean;
  #bounds: { width: number; height: number };

  constructor(options: HandOptions) {
    this.#bounds = { width: options.bounds.width, height: options.bounds.height };
    this.size = faceSize(options.height);

    this.#ownsFaces = options.faces === undefined;
    this.faces =
      options.faces ?? new CardFaceCache(options.renderer, new CardArtRegistry(specArt));

    if (options.prewarm ?? true) this.faces.prewarm(options.cards, this.size);

    this.view = new HandView({
      input: options.input,
      faces: this.faces,
      size: this.size,
      cards: options.cards,
      bounds: this.#bounds,
      ...(options.anchor === undefined ? {} : { anchor: options.anchor }),
      ...(options.curl === undefined ? {} : { curl: options.curl }),
      ...(options.fill === undefined ? {} : { fill: options.fill }),
      ...(options.feel === undefined ? {} : { feel: options.feel }),
      ...(options.onPlay === undefined ? {} : { onPlay: options.onPlay }),
    });
    this.container = this.view.view;
  }

  /**
   * 每帧推进。
   *
   * ⚠️ 挂到**那唯一一个** ticker 回调上（`architecture.md` §7.1），
   * 别自己 `app.ticker.add`。
   */
  update(ticker: Ticker): void {
    this.view.update(ticker);
  }

  /**
   * 画布尺寸变了。
   *
   * ⚠️ 手牌的摆法（弧心、间距、垂度）全是从它推出来的，所以窗口一变就得调它 ——
   * 不需要调用方自己算任何位置。
   */
  resize(bounds: { readonly width: number; readonly height: number }): void {
    this.#bounds = { width: bounds.width, height: bounds.height };
    this.view.setBounds(this.#bounds);
  }

  /**
   * **音乐脉动（Bar 事件）**：所有牌立即放大一下，然后 easeout 缩回去。
   *
   * 主界面里由宿主接音乐的小节（`ui/screenHost.ts` → 对局屏的 `onBar`）
   * 每小节敲一次 —— 热座和联机两屏走的是同一条路。
   *
   * ⚠️ 这一层**不许 import 音频层**：手牌不知道音乐存在，是音乐来敲手牌。
   * 所以它只是一个"跳一下"的口子；谁敲、敲在什么拍子上，都不归它管。
   */
  pulse(): void {
    this.view.pulse();
  }

  // ---- 转发：手牌里那些"状态"和"参数" ----

  get cards(): readonly CardView[] {
    return this.view.hands;
  }

  /** 现在藏起来了几张（诊断用。抽牌动画期间会 >0） */
  get hidden(): readonly number[] {
    return this.view.hidden;
  }

  get layout(): Readonly<HandLayout> {
    return this.view.layout;
  }

  get feel(): Readonly<FeelParams> {
    return this.view.feel;
  }

  get selected(): readonly number[] {
    return this.view.selected;
  }

  get order(): readonly number[] {
    return this.view.order;
  }

  get played(): readonly number[] {
    return this.view.played;
  }

  get hovered(): number {
    return this.view.hovered;
  }

  /** 现在被 hover 的那张牌的定义（悬停说明要用）。没指着任何一张就是 `null` */
  get hoveredDef(): CardDef | null {
    return this.view.hoveredDef;
  }

  /** 现在被 hover 的那张牌在画布上的包围盒（悬停说明定位用） */
  hoveredBox(): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null {
    return this.view.boxOf(this.view.hovered);
  }

  /** 远端点名要看哪一张（`null` = 不看了）。见 `HandView.setRemoteHover` */
  setRemoteHover(cardIndex: number | null): void {
    this.view.setRemoteHover(cardIndex);
  }

  /** 现在被拎起来多远（单位：牌高）。联机时把它发给对面重放拖拽 */
  dragInfo(): {
    readonly card: number;
    readonly dx: number;
    readonly dy: number;
    readonly x: number;
    readonly y: number;
    readonly armed: boolean;
  } | null {
    return this.view.dragInfo();
  }

  /** 现在被按住的是哪张（卡牌索引）。-1 = 没有 */
  get held(): number {
    return this.view.held;
  }

  /** 拖动期间已越过出牌线的那几张（松手会打出）—— 上层据此在松手前 arm */
  dragOut(): readonly CardDef[] {
    return this.view.dragOut();
  }

  /** 第 i 张牌的定义（观众广播用） */
  defAt(index: number): CardDef | null {
    return this.view.defAt(index);
  }

  /** 音乐脉动的当前值 0..1。读数用 */
  get pulseValue(): number {
    return this.view.pulseValue;
  }

  get count(): number {
    return this.view.count;
  }

  applyFeel(next: Partial<FeelParams>): void {
    this.view.applyFeel(next);
  }

  applyLayout(next: { curl?: number; fill?: number }): void {
    this.view.applyLayout(next);
  }

  setCards(cards: readonly CardDef[]): void {
    if (this.#ownsFaces) this.faces.prewarm(cards, this.size);
    this.view.setCards(cards);
  }

  isSelected(index: number): boolean {
    return this.view.isSelected(index);
  }

  toggleSelect(index: number): void {
    this.view.toggleSelect(index);
  }

  selectAll(): void {
    this.view.selectAll();
  }

  clearSelection(): void {
    this.view.clearSelection();
  }

  /**
   * 一切归零：牌回槽位、顺序复位、选中/已打出清空。
   *
   * 正式游戏里用在"新一局 / 重开"；调试页拿它一键复位，省得等牌自己飞回来。
   */
  reset(): void {
    this.view.reset();
  }

  /**
   * **给一张正在飞过来的牌留个位**，返回它该落的那一格的**完整姿态**
   * （坐标 / 旋转 / 尺寸）。
   *
   * 飞进来的那张牌拿它当落点，手牌自己会把扇形重新排成"多一张"的样子 ——
   * 其余牌让出一个空位。牌到位后调 `commitArrival()`。
   *
   * ⚠️ 返回 `null` 只在"手牌一张都没有、而且布局算不出来"这种边界上。
   */
  reserveArrival(): CardPose | null {
    return this.view.reserveArrival();
  }

  /**
   * **新牌到位**：把预留那一格坐实。
   *
   * 调用方紧接着要 `setCards()` 把新牌喂进来 —— 之后手牌画的就是
   * "加完牌"的状态，而飞行的那个视图由调用方删掉。
   */
  commitArrival(): void {
    this.view.commitArrival();
  }

  /** 放弃这次让位（牌没飞过来）：把牌收回去，别留一个空位 */
  cancelArrival(): void {
    this.view.cancelArrival();
  }

  /**
   * **把刚刚拖出去打的那几张放回手上。**
   *
   * `HandView.#play()` 是乐观的（先把牌摘出扇形，再通知上层），而上层有可能
   * 发现这一手不合法。返回有没有真的退回去 —— 空操作时是 `false`。
   * 详细的来由见 `HandView.revert()`。
   */
  revert(): boolean {
    return this.view.revert();
  }

  /**
   * **藏起若干张牌**（卡牌索引），位置照样占着。抽牌动画期间用：
   * 那张牌从抽牌堆飞过来的时候，手上的槽位不该已经坐着它了。
   *
   * ⚠️ 传进去的是"现在哪些不该显示"的**完整**答案（替换，不是叠加）。
   */
  setHidden(cards: readonly number[]): void {
    this.view.setHidden(cards);
  }

  /**
   * **现在能不能操作**（评审：不可操作时手牌无视 hover）。
   *
   * 判据在屏幕那一侧（它知道 `options()` 里有没有用这手牌的选项），
   * 这里只转手给 `HandView`。见 `HandView.#interactive`。
   */
  setInteractive(on: boolean): void {
    this.view.setInteractive(on);
  }

  /** 能不能把牌打出去（false = 只能拖拽换位）。见 `HandView.setPlayable` */
  setPlayable(on: boolean): void {
    this.view.setPlayable(on);
  }

  /** 现在能不能把牌打出去（诊断用）。见 `HandView.setPlayable` */
  get playable(): boolean {
    return this.view.playable;
  }

  destroy(): void {
    this.view.destroy();
    // ⚠️ 只销毁**自己建的**那份缓存。传进来的那份还有别的牌位在用
    if (this.#ownsFaces) this.faces.destroy();
  }
}
